import { createSignal, createEffect, createMemo, onMount, onCleanup, untrack } from "solid-js";
import Map from "../../../../components/charts/Map";
import { setPlaybackSpeed } from "../../../../store/playbackStore";
import { persistantStore } from "../../../../store/persistantStore";
const { selectedClassName, selectedProjectId } = persistantStore;
import { debug } from "../../../../utils/console";
import { sourcesStore } from "../../../../store/sourcesStore";
import { liveSourcesStore } from "../../../../store/liveSourcesStore";
import { streamingStore } from "../../../../store/streamingStore";
import { liveConfigStore } from "../../../../store/liveConfigStore";
import { streamingStatusStore } from "../../../../store/streamingStatusStore";
import { logPageLoad } from "../../../../utils/logging";
import { loadLiveMapSourceIds, saveLiveMapSourceIds } from "../../../../utils/liveSourcePersistence";

export default function MapComponent(props) {
  const [selectedSourceIds, setSelectedSourceIds] = createSignal(new Set());
  let abortController: AbortController | null = null;

  // LiveMapContainer owns streaming init, playback, and timeline — this page only manages source selection.
  onMount(() => {
    void logPageLoad("LiveMap.tsx", "Live Map", "Loaded");
    debug('[LiveMap] 🎬 onMount called');

    abortController = new AbortController();

    streamingStatusStore.initialize();
    liveConfigStore.fetchLiveConfig().catch(() => {});

    if (!props?.isInSplitView) {
      setPlaybackSpeed(1);
    }
  });

  createEffect(() => {
    const status = streamingStatusStore.status();
    liveConfigStore.syncLiveConfigFromStatus({
      streamingStarted: status.streamingStarted,
      simulationActive: status.simulationActive,
      isLiveProcessingActive: status.isLiveProcessingActive,
    });
  });

  onCleanup(() => {
    debug('[LiveMap] 🧹 onCleanup called - aborting all operations');

    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    if (streamingStore.isInitialized) {
      debug('[LiveMap] 🧹 Cleaning up streaming store (unsubscribing from WebSocket)');
      try {
        streamingStore.cleanup();
      } catch (err) {
        debug('[LiveMap] Error during streaming store cleanup:', err);
      }
    }

    liveSourcesStore.clear();
    debug('[LiveMap] ✅ Cleanup complete');
  });

  // Build dataSourcesOptions from sourcesStore
  const dataSourcesOptions = createMemo(() => {
    if (!sourcesStore.isReady()) {
      return [];
    }
    
    const projectSources = sourcesStore.sources();
    if (!projectSources || projectSources.length === 0) {
      return [];
    }
    
    // Create signal getters - use untrack to prevent memo from tracking selectedSourceIds
    const options = untrack(() => {
      return projectSources
        .map((s) => {
          const id = Number(s.source_id);
          if (!Number.isFinite(id)) {
            return null;
          }
          
          const getter = () => selectedSourceIds().has(id);
          const setter = (value) => {
            if (value instanceof Set) {
              setSelectedSourceIds(new Set(Array.from(value).map(Number)));
              return;
            }
            const next = new Set(selectedSourceIds());
            const on = typeof value === 'function' ? value(next.has(id)) : value;
            if (on) next.add(id); else next.delete(id);
            setSelectedSourceIds(next);
          };
          
          return {
            key: `source-${id}`,
            label: s.source_name || `Source ${id}`,
            type: 'toggle',
            signal: [getter, setter]
          };
        })
        .filter(opt => opt !== null);
    });
    
    return options;
  });
  
  // Restore the live map's OWN persisted selection (`liveMapSources`). Seeded once from the shared
  // fleetPerformanceSources selection on first visit, then independent of the report pages.
  //
  // Reads liveSourcesStore first: that store IS this page's selection, kept live so the other map
  // consumers (playbackStore's data-edge clock, RealtimeDataTable) see it without a round trip.
  let hasRestoredSources = false;
  let restoreComplete = false;
  createEffect(() => {
    const options = dataSourcesOptions();
    if (options.length === 0) return;
    if (hasRestoredSources) return;

    const validIds = new Set(
      options
        .map((opt) => {
          const match = opt.key.match(/source-(\d+)/);
          return match ? Number(match[1]) : null;
        })
        .filter((id): id is number => id !== null)
    );
    if (validIds.size === 0) return;

    hasRestoredSources = true;

    void (async () => {
      try {
        const existing = untrack(() => liveSourcesStore.selectedSourceIds());
        const existingValid =
          existing.size > 0 && Array.from(existing).every((id) => validIds.has(id));
        if (existingValid) {
          setSelectedSourceIds(new Set(existing));
          debug('[LiveMap] Using liveSourcesStore selection', { count: existing.size });
          return;
        }

        const restored = await loadLiveMapSourceIds();
        const filtered = new Set(
          Array.from(restored).filter((id) => validIds.has(id))
        );
        if (filtered.size > 0) {
          setSelectedSourceIds(filtered);
          debug('[LiveMap] Restored live sources from user settings', { count: filtered.size });
          return;
        }

        setSelectedSourceIds(new Set());
        debug('[LiveMap] No saved live sources; starting with none selected');
      } finally {
        restoreComplete = true;
      }
    })();
  });

  // Persist to the map's own `liveMapSources` key. Deliberately does not write filterStore /
  // fleetPerformanceSources — toggling a boat here must not re-filter the report pages.
  createEffect(() => {
    if (!restoreComplete) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;

    const cls = selectedClassName?.() || '';
    if (!cls) return;

    void saveLiveMapSourceIds(ids);
  });

  // Sync selectedSourceIds to liveSourcesStore whenever it changes (map-scoped consumers only)
  createEffect(() => {
    const ids = selectedSourceIds();
    liveSourcesStore.setSelectedSourceIds(ids);
  });

  return (
    <div style="position: relative; width: 100%; height: 100%;">
      <Map 
        objectName={props?.objectName} 
        sourceMode={'multi'} 
        liveMode={true}
        dataSourcesOptions={dataSourcesOptions}
      />
    </div>
  );
}
